import React, { useEffect, useRef, useState } from "react";
import { ResponsiveDeviceType } from "../../../widget/layouts/ScreenMedia";
import { ChatLocationContextImport } from "../../../provider/chats/chats/chat_location/ChatLocationContextProvider";
import Center from "../../../widget/common/ui/Center";
import Loader from "../../../widget/common/ui/Loader";
import BottomSheet from "../../../widget/common/ui/BottomSheet";
import { Listing, ListItem } from "../../../widget/common/ui/Listing";
import { textCase } from "../../../service/ui/UI";
import { TextBlock } from "../../../widget/common/ui/Text";
import AppTranslation from "../../../service/app_translation/AppTranslation";
import { IconImageAsset } from "../../../widget/common/ui/Icon";
import AppTheme from "../../../service/app_config/AppTheme";
import SearchInput from "../../../widget/common/form/SearchInput";
import { NearByLocationData } from "../../../model/chats/ChatLocationModel";
import UtilityHelper from "../../../service/utilities/UtilityHelper";

type CurrentCoordinatesType = { lat: number; lng: number };

const ChatLocationContent = () => {
  const type: number = ResponsiveDeviceType();
  const {
    coordinates,
    currentLocationAccuracy,
    handleClickLocation,
    handleOnChange,
    clearSearch,
    searchValue,
    showSearch,
  } = ChatLocationContextImport();

  const mapRef = useRef<HTMLDivElement | null>(null);

  const [fetching, setFetching] = useState<boolean>(true);
  const [fetchedLocations, setFetchedLocations] =
    useState<Array<NearByLocationData> | null>(null);

  const [currentCoordinates, setCurrentCoordinates] =
    useState<CurrentCoordinatesType | null>({
      lat: coordinates?.lat!,
      lng: coordinates?.lng!,
    });

  // 1. Create a Map instance
  // ✅ Map options
  const options: google.maps.MapOptions = {
    //zoomControl: false, // hide zoom buttons
    //scrollwheel: false, // disable zooming with scroll
    //disableDoubleClickZoom: true, // disable zooming on double click
    gestureHandling: "greedy", // disable all gestures (optional)
    fullscreenControl: false, // 🔥 removes fullscreen button
    streetViewControl: false, // optional — removes Street View pegman
    mapTypeControl: false, // optional — removes Map/Satellite toggle
    zoom: 14,

    //gestureHandling: "greedy", // allow free dragging
    //draggable: true, // enable drag
  };

  const searchLocation = () => {
    if (UtilityHelper.isNotEmpty(searchValue) && searchValue.length > 2) {
      if (!window.google || !mapRef.current) return;

      setFetching(true);
      const map = new google.maps.Map(mapRef.current, {
        center: currentCoordinates,
        ...options,
      });

      const service = new google.maps.places.PlacesService(map);

      const request: google.maps.places.FindPlaceFromQueryRequest = {
        query: searchValue,
        fields: ["geometry"],
      };

      service.findPlaceFromQuery(request, (results, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && results) {
          console.log("FindPlaceFromQuery results:", results);

          // Optionally add markers
          results.forEach((place) => {
            if (!place.geometry || !place.geometry.location) return;
            new google.maps.Marker({
              map,
              position: place.geometry.location,
              title: place.name,
            });
          });

          // Optionally center map on first result
          if (results[0].geometry?.location) {
            map.setCenter(results[0].geometry.location);
            searchNearBy(map, {
              lat: results[0].geometry.location.lat(),
              lng: results[0].geometry.location.lng(),
            });
          }
        } else {
          console.error("PlacesService error:", status);
        }
      });
    }
  };

  const searchNearBy = (
    map: google.maps.Map,
    center: CurrentCoordinatesType
  ) => {
    // 2. Create the PlacesService tied to the map
    const service = new google.maps.places.PlacesService(map);

    // 3. Prepare nearbySearch request
    const request: google.maps.places.PlaceSearchRequest = {
      location: center,
      radius: 1500,

      // type: "restaurant",
    };

    // 4. Execute the search
    service.nearbySearch(request, (results, status) => {
      if (status === google.maps.places.PlacesServiceStatus.OK && results) {
        let locationList: Array<NearByLocationData> = [];
        results.forEach((loop: google.maps.places.PlaceResult) => {
          locationList!.push({
            geometry: {
              location: {
                lat: loop.geometry?.location?.lat(),
                lng: loop.geometry?.location?.lng(),
              },
            },
            icon: loop.icon,
            name: loop.name,
            place_id: loop.place_id,
            vicinity: loop.vicinity,
          });
        });

        setFetchedLocations(locationList);
        console.log("Nearby Places:", results);
      } else {
        console.error("PlacesService error:", status);
      }
      setFetching(false);
    });
  };

  const setupMap = () => {
    if (
      currentCoordinates != null &&
      currentCoordinates.lat !== undefined &&
      currentCoordinates.lng !== undefined
    ) {
      setFetching(true);
      if (!window.google || !mapRef.current) return;

      const map = new google.maps.Map(mapRef.current, {
        center: currentCoordinates,
        ...options,
      });

      const marker = new google.maps.Marker({
        position: currentCoordinates,
        draggable: false,
        map,
        title: AppTranslation.text("current_location"),
      });

      // Create an info window (the box)
      const infoWindow = new google.maps.InfoWindow({
        content: `<div>${AppTranslation.text("current_location")}</div>`,
      });

      // Open the info window when marker is clicked
      marker.addListener("click", () => {
        infoWindow.open(map, marker);
      });

      searchNearBy(map, currentCoordinates);
    }
  };

  useEffect(() => {
    setupMap();
    // eslint-disable-next-line
  }, [currentCoordinates]);

  useEffect(() => {
    setCurrentCoordinates({
      lat: coordinates?.lat!,
      lng: coordinates?.lng!,
    });
  }, [coordinates]);

  const listContent: JSX.Element = (
    <>
      {fetching &&
      currentCoordinates != null &&
      currentCoordinates.lat !== undefined &&
      currentCoordinates.lng !== undefined ? (
        <Center className="py-3">{Loader.normal()}</Center>
      ) : (
        <div className={`h-100`}>
          {currentCoordinates != null &&
            currentCoordinates.lat !== undefined &&
            currentCoordinates.lng !== undefined && (
              <div className={`px-4 bgcolor-grey7`}>
                <div
                  className="d-flex align-items-center py-2"
                  role="button"
                  onClick={() =>
                    handleClickLocation(
                      coordinates?.lat!,
                      coordinates?.lng!,
                      ""
                    )
                  }
                >
                  <IconImageAsset
                    name="current_loc"
                    size={30}
                    color={AppTheme.primary}
                  />
                  <div className="ps-2">
                    <TextBlock
                      fontName="bold"
                      fontSize={type === 3 ? "16_17" : "14_15"}
                      textColor="theme-sublabel"
                      maxLine={1}
                    >
                      {AppTranslation.text("send_your_current_location")}
                    </TextBlock>
                    <TextBlock
                      fontName="book"
                      fontSize={type === 3 ? "14_15" : "12_13"}
                      textColor="theme-sublabel"
                      maxLine={1}
                    >
                      accurate to {currentLocationAccuracy.toFixed(1)} m
                    </TextBlock>
                  </div>
                </div>
              </div>
            )}

          {fetchedLocations != null && fetchedLocations.length > 0 && (
            <>
              <div className="px-4">
                <div className="border-bottom py-3">
                  <TextBlock
                    fontName="bold"
                    fontSize={type === 3 ? "16_17" : "14_15"}
                    textColor="theme-sublabel"
                  >
                    {AppTranslation.text("nearby_places")}
                  </TextBlock>
                </div>
              </div>
              <div
                className="px-4"
                style={{
                  overflowY: `auto`,
                  height: `calc(100% - ${type === 3 ? "120px" : "80px"})`,
                }}
              >
                <Listing>
                  {fetchedLocations.map((location, key) => (
                    <ListItem key={key}>
                      <div
                        className="d-flex align-items-center"
                        role="button"
                        onClick={() =>
                          handleClickLocation(
                            location?.geometry?.location?.lat!,
                            location?.geometry?.location?.lng!,
                            location?.name!
                          )
                        }
                      >
                        <IconImageAsset
                          name="location2"
                          size={30}
                          color={AppTheme.primary}
                        />
                        <div className="ps-2">
                          <TextBlock
                            fontName="bold"
                            fontSize={type === 3 ? "16_17" : "14_15"}
                            textColor="theme-sublabel"
                            maxLine={1}
                          >
                            {textCase(location.name!)}
                          </TextBlock>
                          <TextBlock
                            fontName="book"
                            fontSize={type === 3 ? "14_15" : "12_13"}
                            textColor="theme-sublabel"
                            maxLine={1}
                          >
                            {textCase(location.vicinity!)}
                          </TextBlock>
                        </div>
                      </div>
                    </ListItem>
                  ))}
                </Listing>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );

  return (
    <div className="chat-location-map h-100 w-100">
      <div
        className={`w-100 bgcolor-grey position-relative`}
        style={{ height: type === 3 ? 300 : "100%" }}
      >
        {showSearch && (
          <div
            style={{ position: "absolute", zIndex: 8 }}
            className={`p-${type === 3 ? 3 : 4} w-100`}
          >
            <SearchInput
              name="search-map-location"
              placeHolder="search"
              handleOnChange={handleOnChange}
              onClickSuffix={clearSearch}
              value={searchValue}
              autoFocus={false}
              handleEnter={searchLocation}
            />
          </div>
        )}

        {coordinates == null ? (
          <Center className="h-100">{Loader.normal()}</Center>
        ) : (
          <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
        )}
      </div>
      {type === 3 ? (
        <div
          style={{
            maxHeight: 370,
            overflowX: "hidden",
            overflowY: "auto",
            marginTop: -20,
          }}
          className="border-top-left-radius bgcolor-white border-top-right-radius  py-3 position-relative"
        >
          {listContent}
        </div>
      ) : (
        <BottomSheet className="px-0">{listContent}</BottomSheet>
      )}
    </div>
  );
};

export default ChatLocationContent;
